import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePipelineContext } from "../../../contexts/PipelineContext";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { getHeaders } from "../../../utils/apiUtils";
import axios from "axios";
import { BASE_URL } from "../../../config/apiConfig";
import { showAlert } from "../../common/userFeedback/CustomAlert";
import UserMultiSelect from "../../forms/Dropdowns/UserMultiSelect";
import UserGroupMultiSelect from "../../forms/Dropdowns/UserGroupMultiSelect";
import PipelineJobMultiSelect from "../../forms/Dropdowns/PipelineJobMultiSelect";
import UserTable from "../../layout/BasicUserTable";
import { JobConfigurationProvider, useJobConfigurationContext } from "../../../contexts/JobConfigurationContext";

function PipelineDetails({ jobConfigurationSection=null, userTableHeight="25vh" }) {
  const {
    name,
    setName,
    handleTargetQuantityChange,
    targetQuantity,
    datasets,
    setDatasets,
    pipelineId,
    orderedJobs,
    updateJob
  } = usePipelineContext();
  const [allDatasets, setAllDatasets] = useState([]);
  const [localSelectedUsers, setLocalSelectedUsers] = useState([]);
  const [purposelySelectedUsers, setPurposelySelectedUsers] = useState([]);
  const [selectedUserGroups, setSelectedUserGroups] = useState([]);
  const [pipelineJobs, setPipelineJobs] = useState([]);
  const [pipelineIds, setPipelineIds] = useState([]);
  const [updatingPipeline, setUpdatingPipeline] = useState(false);
  const [userTableHasChanged, setUserTableHasChanged] = useState(false);
  const { activeJob, setActiveJob, setSelectedUsers, selectedUsers } = useJobConfigurationContext();
  const prevLocalSelectedUsersRef = useRef([]);

  const handleNameChange = (event) => {
    setName(event.target.value);
  };

  const handleDatasetChange = (event) => {
    setDatasets(event.target.value);
  };

  const fetchDatasets = async () => {
    const headers = await getHeaders();
    const response = await axios
      .get(`${BASE_URL}tools/admin/datasets/listall`, headers)
      .then((response) => {
        setAllDatasets(response.data.data.filter(x => !x.is_archived));
      })
      .catch((error) => {
        showAlert(error);
      });
  };

  useEffect(() => {
    console.log("PipelineDetails - selectedUsers updated:", selectedUsers);
  }, [selectedUsers]);

  const updatePipelineJobUsers = useCallback(() => {
    setUpdatingPipeline(true);
    // Filter orderedJobs to only include jobs that exist in pipelineJobs
    const selectedJobs = orderedJobs.filter(job =>
      pipelineJobs.some(pj => pj.id === job.id || pj.pipeline_job_id === job.id)
    );
    
    if (selectedJobs.length > 0) {
      if (activeJob) {
        // Merge active job users, localSelectedUsers, and purposelySelectedUsers (ensuring uniqueness)
        const activeJobUpdatedUsers = [
          ...new Map(
            [...(activeJob.users || []), ...localSelectedUsers, ...purposelySelectedUsers]
              .map(user => [user.id, user]) // Ensure uniqueness by id
          ).values(),
        ];
  
        setActiveJob({ ...activeJob, users: activeJobUpdatedUsers });
      }
  
      // Update job users in the relevant pipeline jobs
      orderedJobs.forEach(job => {
        console.log(`ababab`)
        const pipelineJob = pipelineJobs.find(pj => pj.id === job.id || pj.pipeline_job_id === job.id);
        console.log(`pipelineJob: ${JSON.stringify(pipelineJob)}`)
        if (pipelineJob) {
          // Merge job users, localSelectedUsers, and purposelySelectedUsers (ensuring uniqueness)
          const pipelineJobUpdatedUsers = [
            ...new Map(
              [...localSelectedUsers, ...purposelySelectedUsers]
                .map(user => [user.id, user]) // Ensure uniqueness by id
            ).values(),
          ];
  
          console.log(`pjid: ${pipelineJob.pipeline_job_id} pipelineJobUpdatedUsers: ${JSON.stringify(pipelineJobUpdatedUsers)}`);
          updateJob(job.id, { ...job, users: pipelineJobUpdatedUsers });
        }
      });
  
      // Ensure selected users include both localSelectedUsers and purposelySelectedUsers
      setSelectedUsers([
        ...new Map(
          [...localSelectedUsers, ...purposelySelectedUsers]
            .map(user => [user.id, user]) // Ensure uniqueness by id
        ).values(),
      ]);
    }
    
    setUpdatingPipeline(false);
    setUserTableHasChanged(false);
  }, [activeJob, localSelectedUsers, orderedJobs, pipelineJobs, purposelySelectedUsers, setActiveJob, setSelectedUsers, updateJob]);

  const fetchPipelineJobUsers = useCallback(() => {
    let uniqueUsers = Array.from(
      new Map([...purposelySelectedUsers].map(user => [user.id, user])).values()
    );

    if (!pipelineJobs || pipelineJobs.length === 0) {
      setLocalSelectedUsers(uniqueUsers);
      return;
    }
    
    // Filter orderedJobs to only include jobs that exist in pipelineJobs
    const selectedJobs = orderedJobs.filter(job => 
      pipelineJobs.some(pj => pj.id === job.id || pj.pipeline_job_id === job.id)
    );
  
    console.log("Selected Jobs:", JSON.stringify(selectedJobs));
  
    if (selectedJobs.length === 0) {
      setLocalSelectedUsers(uniqueUsers);
      return;
    }
  
    // Extract sets of user IDs from each selected job
    const allJobUsers = selectedJobs.map(job => new Set(job.users.map(user => user.id)));
    
    console.log("All Job Users:", allJobUsers.map(set => [...set]));
  
    // Find common user IDs across all selected jobs
    const commonUserIds = allJobUsers.length > 0
      ? allJobUsers.reduce((common, jobUsers) => 
          new Set([...common].filter(id => jobUsers.has(id)))
        )
      : new Set();
      
    console.log("Common User IDs:", [...commonUserIds]);
      
    // Retrieve user objects for common users
    const commonUsers = selectedJobs
      .flatMap(job => job.users)
      .filter(user => commonUserIds.has(user.id));

    // Ensure uniqueness & add users already in purposelySelectedUsers
    uniqueUsers = Array.from(
      new Map([...purposelySelectedUsers, ...commonUsers].map(user => [user.id, user])).values()
    );

    console.log("Final Unique Users:", uniqueUsers);

    setLocalSelectedUsers(uniqueUsers);
  }, [orderedJobs, pipelineJobs, purposelySelectedUsers]);

  useEffect(() => {
    fetchDatasets();
  }, []);

  useEffect(() => {
    setPurposelySelectedUsers([])
  }, [pipelineJobs])

  useEffect(() => {
    // Create sets of user IDs for both previous and current localSelectedUsers
    const prevUserIds = new Set(prevLocalSelectedUsersRef.current.map(user => user.id));
    const currentUserIds = new Set(localSelectedUsers.map(user => user.id));

    // Check if user IDs are different
    const hasChanged = prevUserIds.size !== currentUserIds.size || 
                       [...prevUserIds].some(id => !currentUserIds.has(id)) || 
                       [...currentUserIds].some(id => !prevUserIds.has(id));
    setUserTableHasChanged(hasChanged);

    if (hasChanged && prevUserIds.size >= currentUserIds.size) {
      prevLocalSelectedUsersRef.current = localSelectedUsers;  // Update reference to latest value of localSelectedUsers
      //console.log('User IDs have changed');
      //console.log('Previous User IDs:', [...prevUserIds]);
      //console.log('Current User IDs:', [...currentUserIds]);
      //console.log(`purposelySelectedUserspurposelySelectedUsers: ${purposelySelectedUsers.map(z => z.id)}`)
      //console.log(`purposelySelectedUserspurposelySelectedUsers.filter(x => localSelectedUsers.map(user => user.id).includes(x.id)): ${purposelySelectedUsers.filter(x => localSelectedUsers.map(user => user.id).includes(x.id)).map(z => z.id)}`)
      setPurposelySelectedUsers(purposelySelectedUsers.filter(x => localSelectedUsers.map(user => user.id).includes(x.id)))
    }
  }, [localSelectedUsers, purposelySelectedUsers]); // Watch for changes in localSelectedUsers
  
  useEffect(() => {
    if (JSON.stringify(prevLocalSelectedUsersRef.current) !== JSON.stringify(localSelectedUsers)) {
      prevLocalSelectedUsersRef.current = localSelectedUsers;  // Save latest value of localSelectedUsers
      //updatePipelineJobUsers();  // Trigger job update only when users actually change
    }
  }, [localSelectedUsers]);

  useEffect(() => {
    if (purposelySelectedUsers) {
      let localSelectedUsersHasChanged = false;
      const uniqueUsers = [
        ...new Map(
          [...(localSelectedUsers || []), ...purposelySelectedUsers]
            .map(user => [user.id, user]) // Ensure uniqueness based on id
        ).values(),
      ]
      const uniqueUserIds = new Set(uniqueUsers.map(user => user.id));
      const localSelectedUserIds = new Set(localSelectedUsers.map(user => user.id));

      if (!(uniqueUserIds.size === localSelectedUserIds.size && 
        [...uniqueUserIds].every(id => localSelectedUserIds.has(id))))
      {
        setLocalSelectedUsers(prevUsers => [
          ...new Map(
            [...(prevUsers || []), ...purposelySelectedUsers]
              .map(user => [user.id, user]) // Ensure uniqueness based on id
          ).values(),
        ]);
        localSelectedUsersHasChanged = true;
      }

      console.log(`localSelectedUsersHasChanged: ${localSelectedUsersHasChanged}`)
      
      if (localSelectedUsersHasChanged || userTableHasChanged)
      {
        updatePipelineJobUsers();
      }
    }
  }, [purposelySelectedUsers]);

  useEffect(() => {
    if (pipelineId)
    {
      setPipelineIds([pipelineId])
    }
  }, [pipelineId])

  useEffect(() => {
    if (!updatingPipeline)
    {
      fetchPipelineJobUsers()
    }
  }, [pipelineJobs, orderedJobs, updatingPipeline, purposelySelectedUsers])

  useEffect(() => {
    console.log(`orderedJobs123: ${JSON.stringify(orderedJobs)}`)
    if (orderedJobs.length === 0)
    {
      setPipelineJobs([])
    } else {
      console.log(`pipelineJobspipelineJobs: ${JSON.stringify(pipelineJobs)}`)
      setPipelineJobs([
        ...new Map(
          pipelineJobs
            .filter(x => orderedJobs.some(y => y.id === x.id)) // Ensure jobs exist in orderedJobs
            .map(job => [job.id, job]) // Create a Map with unique IDs
        ).values()
      ]);
    }
  }, [orderedJobs])

  return (
    <Box
      component="form"
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flex: 1,
        flexDirection: jobConfigurationSection ? "row" : "column",
      }}
    >
      <Box sx={jobConfigurationSection ? {
        width: "100%"
      } : {mt: 0}}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="name"
          label="Pipeline Name"
          name="name"
          value={name}
          onChange={handleNameChange}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          id="targetQuantity"
          label="Target Quantity"
          name="targetQuantity"
          value={targetQuantity}
          onChange={handleTargetQuantityChange}
          sx={{mt: 0.5}}
        />

        <FormControl fullWidth margin="normal" 
          sx={{mt: 0.5}}>
          <InputLabel shrink={true} id="dataset-label">
            Dataset
          </InputLabel>
          <Select
            labelId="dataset-label"
            id="dataset"
            label="Dataset"
            notched={true}
            value={datasets}
            onChange={handleDatasetChange}
          >
            {allDatasets.map((dataset) => (
              <MenuItem value={dataset.id}>{dataset.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {jobConfigurationSection && (jobConfigurationSection)}
      </Box>

      <Box sx={jobConfigurationSection ? {
        mt: -4.5,
        ml: 4,
        width: "100%"
      } : {}}>
        <Typography variant="h3" sx={{mt: 2, mb: 1}}>
          ADD USERS TO JOBS
        </Typography>

        <PipelineJobMultiSelect
          pipelineJobs={orderedJobs}
          width={null}
          sx={{ mb: 1}}
          selected={pipelineJobs}
          onSelectedChange={setPipelineJobs}
          pipelineIds={pipelineIds}
          jobNames={[]}
          label={"Select Pipeline Jobs"}
        />
        <UserGroupMultiSelect sx={{mt: 1}} disabled={!(pipelineJobs && pipelineJobs.length > 0)}
          setSelectedUsers={setPurposelySelectedUsers} 
        />
        <UserMultiSelect  sx={{mt: 1}} disabled={!(pipelineJobs && pipelineJobs.length > 0)}
          setSelectedUsers={setPurposelySelectedUsers} 
        />
        <UserTable  sx={{mt: 1}}
          height={userTableHeight}
          users={localSelectedUsers} 
          setUsers={setLocalSelectedUsers} 
        />
      </Box>
    </Box>
  );
}

export default PipelineDetails;
